import React, { useEffect, useState } from "react";
import AttendanceNew from "../HomeComponents/AttendanceNew";
import FullEmployeeListWithAttendance from "../HomeComponents/FullEmployeeListWithAttendance";
import JobStatus from "../HomeComponents/JobStatus";
import OnLeave from "../HomeComponents/OnLeave";
import axios from "axios";
import { base_url } from "../../utils/AxiosConfig";
import { X, Users, UserCheck, UserX, Palmtree } from "lucide-react";

// ── Reusable Modal Shell ─────────────────────────────────────────────────────
const AttendanceModal = ({ title, icon: Icon, accentColor, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
    <div className="relative flex flex-col w-full max-w-xl max-h-[80vh] bg-white rounded-2xl border border-slate-100 shadow-2xl overflow-hidden">
      {/* Header */}
      <div className={`flex flex-shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4`}>
        <div className="flex items-center gap-3">
          <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${accentColor}`}>
            <Icon className="h-4 w-4 text-white" />
          </div>
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
        </div>
        <button
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={16} />
        </button>
      </div>
      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto scrollbar-none p-4">
        {children}
      </div>
    </div>
  </div>
);

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, iconBg, textColor, ringColor, onClick, loading }) => (
  <button
    onClick={onClick}
    className={`
      group relative flex flex-col items-center justify-center gap-2
      rounded-2xl border border-slate-100 bg-white p-4
      shadow-sm transition-all duration-200
      hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5
      focus:outline-none focus:ring-2 ${ringColor} focus:ring-offset-2
      w-full cursor-pointer
    `}
  >
    <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${iconBg} transition-transform duration-200 group-hover:scale-110`}>
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div className="text-center">
      {loading ? (
        <div className="mx-auto mb-1 h-7 w-10 animate-pulse rounded-lg bg-slate-100" />
      ) : (
        <p className={`text-2xl font-bold leading-none ${textColor}`}>{value}</p>
      )}
      <p className="mt-1 text-xs font-medium text-slate-500">{label}</p>
    </div>
    {/* Subtle hover indicator */}
    <span className="absolute bottom-2 right-2 text-[9px] font-medium tracking-wide text-slate-300 opacity-0 transition-opacity group-hover:opacity-100">
      View →
    </span>
  </button>
);

// ── Component ────────────────────────────────────────────────────────────────
const AttendanceBubbleChart = () => {
  const [onDutyCount, setOnDutyCount] = useState(0);
  const [leaveCounts, setLeaveCounts] = useState(null);
  const [absenteesCount, setAbsenteesCount] = useState(0);
  const [onLeaveCount, setOnLeaveCount] = useState(0);
  const [loading, setLoading] = useState(true);

  console.log("in parent bubble onDutyCount", onDutyCount);

  const handleOnLeaveCount = (onLeaveCnt) => {
    setOnLeaveCount(onLeaveCnt);
    console.log(onLeaveCnt);
  };

  const handleOnDutyCount = (onLeaveCnt) => {
    console.log("onduty", onLeaveCnt);
    setOnDutyCount(onLeaveCnt);
  };

  const handleAbsenteesCount = (count) => {
    console.log("count:", count);
  };

  const {
    onDuty = 0,
    vacation = 0,
    sickLeave = 0,
    dayOff = 0,
    wfh = 0,
    businessTrip = 0,
  } = leaveCounts || {};

  const [showAbsenteesPopup, setShowAbsenteesPopup] = useState(false);
  const [showOnDutyPopup, setShowOnDutyPopup] = useState(false);
  const [showOnLeavePopup, setShowOnLeavePopup] = useState(false);

  const onAbsenteesClick = () => setShowAbsenteesPopup(true);
  const onOnDutyClick = () => setShowOnDutyPopup(true);
  const onOnLeaveClick = () => {
    console.log("onleave click");
    setShowOnLeavePopup(true);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([
        fetchAbsentAttendanceData(),
        fetchOnDutyAttendanceData(),
        fetchOnLeaveAttendanceData(),
      ]);
      setLoading(false);
    };
    load();
  }, []);

  const storedUserDataArray =
    JSON.parse(localStorage.getItem("BondTimeuserDataArray")) || [];
  let companyCode = null;
  if (storedUserDataArray) {
    companyCode = storedUserDataArray[0].cmpcode;
  }

  const fetchAbsentAttendanceData = async () => {
    try {
      const response = await axios.get(
        `${base_url}/api/CpaysCount/${companyCode}/ATTENDANCEDAILY_ABS/-/-`,
      );
      if (response.status === 200) {
        console.log("attendance api data", response.data);
        setAbsenteesCount(response?.data[0].Cnt);
      }
    } catch (error) {
      console.log("fetchAttendanceDataErr", error);
    }
  };

  const fetchOnDutyAttendanceData = async () => {
    try {
      const response = await axios.get(
        `${base_url}/api/CpaysCount/${companyCode}/ATTENDANCEDAILY/-/-`,
      );
      if (response.status === 200) {
        console.log("attendance api data", response.data);
        setOnDutyCount(response?.data[0].Cnt);
      }
    } catch (error) {
      console.log("fetchAttendanceDataErr", error);
    }
  };

  const fetchOnLeaveAttendanceData = async () => {
    try {
      const response = await axios.get(
        `${base_url}/api/CpaysCount/${companyCode}/ATTENDANCEDAILY_LEAVE/-/-`,
      );
      if (response.status === 200) {
        console.log("attendance api data", response.data);
        setOnLeaveCount(response?.data[0].Cnt);
      }
    } catch (error) {
      console.log("fetchAttendanceDataErr", error);
    }
  };

  const total = onDutyCount + absenteesCount + onLeaveCount;
  const onDutyPct = total > 0 ? Math.round((onDutyCount / total) * 100) : 0;

  return (
    <>
      {/* ── Widget Card ──────────────────────────────────────── */}
      <div className="flex h-full w-full flex-col bg-white rounded-2xl overflow-hidden">

        {/* Card Header */}
        <div className="flex items-center justify-between  border-slate-100 px-2 pt-5 pb-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
              Today
            </p>
            <h2 className="text-base font-bold text-slate-900 leading-tight mt-0.5">
              Attendance
            </h2>
          </div>
        </div>

        <div className="flex justify-left w-30 ml-2 ">

          {/* Attendance rate pill */}
          {!loading && total > 0 && (
            <div className="flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="text-xs font-semibold text-emerald-700">{onDutyPct}% present</span>
            </div>
          )}
          </div>

        {/* Stat Cards */}
        <div className="flex flex-col gap-3 p-4">
          <StatCard
            label="On Duty"
            value={onDutyCount}
            icon={UserCheck}
            iconBg="bg-blue-600"
            textColor="text-blue-600"
            ringColor="focus:ring-blue-500"
            onClick={onOnDutyClick}
            loading={loading}
          />
          {/* <div className="grid grid-cols-2 gap-3"> */}
            <StatCard
              label="Absentees"
              value={absenteesCount}
              icon={UserX}
              iconBg="bg-red-500"
              textColor="text-red-500"
              ringColor="focus:ring-red-400"
              onClick={onAbsenteesClick}
              loading={loading}
            />
            <StatCard
              label="On Leave"
              value={onLeaveCount}
              icon={Palmtree}
              iconBg="bg-emerald-500"
              textColor="text-emerald-600"
              ringColor="focus:ring-emerald-400"
              onClick={onOnLeaveClick}
              loading={loading}
            />
          {/* </div> */}
        </div>

        {/* Progress bar */}
        {!loading && total > 0 && (
          <div className="px-4 pb-4 mt-auto">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs text-slate-400">Workforce breakdown</span>
              <span className="text-xs font-medium text-slate-500">{total} total</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="flex h-full">
                <div
                  className="bg-blue-500 transition-all duration-500"
                  style={{ width: `${(onDutyCount / total) * 100}%` }}
                />
                <div
                  className="bg-red-400 transition-all duration-500"
                  style={{ width: `${(absenteesCount / total) * 100}%` }}
                />
                <div
                  className="bg-emerald-400 transition-all duration-500"
                  style={{ width: `${(onLeaveCount / total) * 100}%` }}
                />
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3">
              {[
                { label: "Duty", color: "bg-blue-500" },
                { label: "Absent", color: "bg-red-400" },
                { label: "Leave", color: "bg-emerald-400" },
              ].map(({ label, color }) => (
                <div key={label} className="flex items-center gap-1">
                  <span className={`h-1.5 w-1.5 rounded-full ${color}`} />
                  <span className="text-[10px] text-slate-400">{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Absentees Modal ───────────────────────────────────── */}
      {showAbsenteesPopup && (
        <AttendanceModal
          title="Absentees"
          icon={UserX}
          accentColor="bg-red-500"
          onClose={() => setShowAbsenteesPopup(false)}
        >
          <AttendanceNew
            onAbsenteesCountChange={handleAbsenteesCount}
            onAbsentCountChange={handleAbsenteesCount}
          />
        </AttendanceModal>
      )}

      {/* ── On Duty Modal ─────────────────────────────────────── */}
      {showOnDutyPopup && (
        <AttendanceModal
          title="On Duty"
          icon={UserCheck}
          accentColor="bg-blue-600"
          onClose={() => setShowOnDutyPopup(false)}
        >
          <FullEmployeeListWithAttendance onDutyCountChange={handleOnDutyCount} />
        </AttendanceModal>
      )}

      {/* ── On Leave Modal ────────────────────────────────────── */}
      {showOnLeavePopup && (
        <AttendanceModal
          title="On Leave"
          icon={Palmtree}
          accentColor="bg-emerald-500"
          onClose={() => setShowOnLeavePopup(false)}
        >
          <OnLeave onLeaveCountChange={handleOnLeaveCount} />
        </AttendanceModal>
      )}

      {/* Hidden legacy component preserved from original */}
      <div style={{ display: "none" }}>
        {/* JobStatus intentionally hidden — preserved from original */}
      </div>
    </>
  );
};

export default AttendanceBubbleChart;